import { useState, useRef } from 'react'
import { toast } from 'sonner'
import useQueryGetAdminAssets from '@/modules/admin-assets/api/hooks/useQueryGetAdminAssets'
import useMutationUpdateAdminAssets from '@/modules/admin-assets/api/hooks/useMutationUpdateAdminAssets'
import { FileService } from '@/core/api/services/FileService'
import showErrorToast from '@/common/utils/showErrorToast'

const MAX_FILE_SIZE_MB = 5
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

export function useUploadSound() {
	const { data: adminAssets, isLoading } = useQueryGetAdminAssets()
	const { mutateAsync: updateAdminAssets, isPending: isSaving } =
		useMutationUpdateAdminAssets()

	const [isUploading, setIsUploading] = useState(false)
	const [selectedFile, setSelectedFile] = useState<File | null>(null)
	const fileInputRef = useRef<HTMLInputElement>(null)

	const currentSoundUrl = adminAssets?.withdrawSoundUrl ?? null

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (!file) return

		if (!file.name.toLowerCase().endsWith('.mp3')) {
			showErrorToast('รองรับเฉพาะไฟล์ MP3 เท่านั้น')
			return
		}

		if (file.size > MAX_FILE_SIZE_BYTES) {
			showErrorToast(`ขนาดไฟล์ใหญ่เกินไป (สูงสุด ${MAX_FILE_SIZE_MB}MB)`)
			return
		}

		setSelectedFile(file)
	}

	const handlePreview = (soundUrl?: string | null) => {
		const url = soundUrl ?? currentSoundUrl ?? '/withdraw-pending.mp3'
		const audio = new Audio(url)
		audio.play().catch(() => {
			showErrorToast('ไม่สามารถเล่นไฟล์เสียงได้')
		})
	}

	const handleUpload = async () => {
		if (!selectedFile) return

		try {
			setIsUploading(true)

			const { presignedUrl, fileUrl } =
				await FileService.getWithdrawSoundUploadUrl()

			await FileService.uploadFileToPresignedUrl(presignedUrl, selectedFile)

			await updateAdminAssets({ withdrawSoundUrl: fileUrl })

			setSelectedFile(null)
			if (fileInputRef.current) fileInputRef.current.value = ''

			toast.success('อัปโหลดเสียงแจ้งเตือนเรียบร้อยแล้ว')
		} catch {
			showErrorToast('ไม่สามารถอัปโหลดไฟล์เสียงได้ กรุณาลองใหม่อีกครั้ง')
		} finally {
			setIsUploading(false)
		}
	}

	const handleCancelSelection = () => {
		setSelectedFile(null)
		if (fileInputRef.current) fileInputRef.current.value = ''
	}

	const handleReset = async () => {
		try {
			await updateAdminAssets({ withdrawSoundUrl: null })
			toast.success('รีเซ็ตเป็นเสียงเริ่มต้นเรียบร้อยแล้ว')
		} catch {
			showErrorToast('ไม่สามารถรีเซ็ตเสียงแจ้งเตือนได้ กรุณาลองใหม่อีกครั้ง')
		}
	}

	const handleUploadClick = () => {
		fileInputRef.current?.click()
	}

	return {
		isLoading,
		isUploading: isUploading || isSaving,
		currentSoundUrl,
		selectedFile,
		fileInputRef,
		handleFileSelect,
		handlePreview,
		handleUpload,
		handleCancelSelection,
		handleReset,
		handleUploadClick,
	}
}
